import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { motion } from 'framer-motion';
import '../assets/styles/HomePageBase.css';
import '../assets/styles/LandingLayout.css';

// Import screenshot images reused from HomePageBase
import profileSetupImg from '../assets/screenshots/profile-setup.png';
import profileSelectYourTextbookImg from '../assets/screenshots/profile-select-your-textbook.png';
import selectLessonAndDifficultyImg from '../assets/screenshots/select-lesson-and-difficulty.png';
import startLessonImg from '../assets/screenshots/start-lesson.png';
import homeworkStartImg from '../assets/screenshots/homework-start.png';
import homeworkStepByStepImg from '../assets/screenshots/homework-step-by-step.png';

const ImagePreviews = ({ variant = 'public' }) => {
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile devices
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const slides = useMemo(() => [
    {
      id: 'profile-setup',
      title: 'Profile Setup',
      description: 'Set up your profile to get personalised lessons',
      linkTo: variant === 'auth' ? '/profile' : '/early-access',
      image: profileSetupImg,
      alt: 'Profile Setup Page'
    },
    {
      id: 'textbook-selection',
      title: 'Select Your Textbook',
      description: 'Choose your textbook for aligned lessons',
      linkTo: variant === 'auth' ? '/profile/textbook' : '/early-access',
      image: profileSelectYourTextbookImg,
      alt: 'Textbook Selection Page'
    },
    {
      id: 'lesson-selection',
      title: 'Select Lesson and Difficulty from Textbook',
      description: 'Pick a lesson from your textbook chapters and difficulty level that suits you',
      linkTo: variant === 'auth' ? '/tutor/select-lesson' : '/early-access',
      image: selectLessonAndDifficultyImg,
      alt: 'Lesson Selection Page'
    },
    {
      id: 'ai-chat',
      title: 'AI Tutoring in Action',
      description: 'Get textbook aligned lessons that are personalised to your level',
      linkTo: variant === 'auth' ? '/tutor' : '/early-access',
      image: startLessonImg,
      alt: 'AI Tutor Chat Interface'
    },
    {
      id: 'homework-help',
      title: 'Homework Help',
      description: 'Get on demand support for your math problems',
      linkTo: variant === 'auth' ? '/homework-help' : '/early-access',
      image: homeworkStartImg,
      alt: 'Homework Help Page'
    },
    {
      id: 'step-by-step',
      title: 'Step-by-Step Solutions',
      description: 'Upload images and ask questions',
      linkTo: variant === 'auth' ? '/homework-help' : '/early-access',
      image: homeworkStepByStepImg,
      alt: 'Step-by-step math solutions'
    },
  ], [variant]);

  const [currentSlide, setCurrentSlide] = useState(0);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  }, [slides.length]);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  }, [slides.length]);

  const goToSlide = useCallback((index) => {
    setCurrentSlide(index);
  }, []);

  useEffect(() => {
    const interval = setInterval(nextSlide, isMobile ? 8000 : 6000);
    return () => clearInterval(interval);
  }, [nextSlide, isMobile]);

  const currentSlideContent = useMemo(() => {
    const slide = slides[currentSlide];
    return (
      <div className="demo-screenshot">
        <img
          src={slide.image}
          alt={slide.alt}
          className="screenshot-image"
          loading="lazy"
          decoding="async"
        />
      </div>
    );
  }, [slides, currentSlide]);

  return (
    <div className="image-preview-page">
      <div className="showcase-preview landing-section">
        {isMobile ? (
          <div className="preview-slide">
            <div className="preview-content">
              <div className="preview-text">
                <h3 className="preview-title">{slides[currentSlide].title}</h3>
                <p className="preview-description">{slides[currentSlide].description}</p>
              </div>
              <div className="preview-visual">{currentSlideContent}</div>
            </div>
          </div>
        ) : (
          <motion.div
            key={currentSlide}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="preview-slide"
          >
            <div className="preview-content">
              <div className="preview-text">
                <h3 className="preview-title">{slides[currentSlide].title}</h3>
                <p className="preview-description">{slides[currentSlide].description}</p>
              </div>
              <div className="preview-visual">{currentSlideContent}</div>
            </div>
          </motion.div>
        )}
      </div>
      <div className="showcase-navigation">
        <button className="nav-arrow nav-prev" onClick={prevSlide} aria-label="Previous slide">«</button>
        <div className="slide-indicators">
          {slides.map((_, index) => (
            <button
              key={index}
              className={`slide-indicator ${index === currentSlide ? 'active' : ''}`}
              onClick={() => goToSlide(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
        <button className="nav-arrow nav-next" onClick={nextSlide} aria-label="Next slide">»</button>
      </div>
    </div>
  );
};

export default ImagePreviews;
